import { useTranslation } from "react-i18next";
import { Row, Col, Dropdown } from "react-bootstrap";
import Schedule from "@serie3/mymediaplanner/Modules/Social/Entity/Schedule";
import NewModal from "../../../shared/Modals/NewModal";
import SaveButton from "../../../shared/Buttons/SaveButton";
import DateTimeField from "../../../Form/DateTimeField";
import Timezones from "../../../Form/Timezones";
import ScheduleForm from "@serie3/mymediaplanner/Modules/Social/Forms/ScheduleForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedin, faYoutube, faMeta, faTiktok } from "@fortawesome/free-brands-svg-icons";
import { faUser, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import YoutubeIcon from "../../../shared/YoutubeIcon";
import UserAvatar from "../../../shared/UserAvatar";

const ALL_PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", icon: faLinkedin, color: "#0077b5" },
  { key: "youtube",  label: "YouTube",  icon: faYoutube,  color: "#ff0000" },
  { key: "meta",     label: "Meta",     icon: faMeta,     color: "#0081fb" },
  { key: "tiktok",   label: "TikTok",   icon: faTiktok,   color: "#010101" },
];

const PlatformMultiSelect = ({ formik, label, allPlatforms }) => {
  const platforms = allPlatforms ? ALL_PLATFORMS : ALL_PLATFORMS.filter((p) => p.key === "linkedin");
  const selected = formik.values.platforms ?? [];

  const toggle = (key) => {
    if (selected.includes(key)) {
      formik.setFieldValue("platforms", selected.filter((p) => p !== key));
    } else {
      formik.setFieldValue("platforms", [...selected, key]);
    }
  };

  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 6, display: "block" }}>
        {label}
      </label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {platforms.map(({ key, label: platformLabel, icon, color }) => {
          const isSelected = selected.includes(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                gap: 8, padding: "10px 16px", borderRadius: 10, cursor: "pointer",
                border: isSelected ? `2px solid ${color}` : "2px solid #e0e4ea",
                background: isSelected ? `${color}18` : "#fff",
                color: isSelected ? color : "#9299a8",
                transition: "all 0.15s ease",
                minWidth: 80,
              }}
            >
              {key === "youtube"
                ? <YoutubeIcon height={22} />
                : <FontAwesomeIcon icon={icon} style={{ fontSize: 22 }} />}
              <span style={{ fontSize: 11, fontWeight: 600 }}>{platformLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** One member as shown in the picker: avatar, name, and the group that connects you. */
const CandidateRow = ({ candidate, muted = false }) => (
  <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
    <UserAvatar
      userId={candidate.id}
      name={candidate.name || candidate.username}
      hasPicture={candidate.has_profile_picture !== false}
      size={28}
    />
    <span style={{ minWidth: 0, display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: muted ? "#74788d" : "#1a1f2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {candidate.name || candidate.username}
      </span>
      {candidate.group?.name && (
        <span style={{ fontSize: 11, color: "#74788d", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {candidate.group.name}
        </span>
      )}
    </span>
  </span>
);

const OwnAccountRow = ({ label }) => (
  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <span
      style={{
        width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
        background: "#eef2f7", color: "#74788d",
        display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12,
      }}
      aria-hidden="true"
    >
      <FontAwesomeIcon icon={faUser} />
    </span>
    <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1f2e" }}>{label}</span>
  </span>
);

/**
 * "Publish on" picker: own account, or one of the group members the post may be
 * planned for, each with their profile picture. Only rendered when there are
 * candidates, i.e. the post is shared in a collaboration group. The target
 * cannot be changed on an existing schedule (delete and plan again instead),
 * so it is read-only on edit.
 */
const PublishAsSelect = ({ formik, candidates, editData }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(editData?.id);
  const current = formik.values.publish_as ?? "";
  const ownLabel = t("Backend.Post.Schedule.OwnAccount", "My own account");

  let selected = candidates.find((c) => String(c.id) === String(current)) ?? null;
  if (!selected && current && isEdit) {
    // The schedule's target may no longer be a candidate (left the group);
    // still show who it is, and let the avatar endpoint decide if there is an image.
    const target = editData?.publish_as;
    selected = {
      id: target?.id ?? current,
      name: target?.name,
      username: target?.username ?? String(current),
    };
  }

  const choose = (value) => formik.setFieldValue("publish_as", value);

  return (
    <div>
      <label htmlFor="schedule-publish-as" style={{ fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 6, display: "block" }}>
        {t("Backend.Post.Schedule.PublishOn", "Publish on the account of")}
      </label>
      <Dropdown>
        <Dropdown.Toggle
          id="schedule-publish-as"
          as="button"
          type="button"
          bsPrefix="publish-as-toggle"
          disabled={isEdit}
          className="form-control"
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            height: "auto", minHeight: 42, padding: "6px 12px", textAlign: "left",
            background: isEdit ? "#f8f9fa" : "#fff", cursor: isEdit ? "not-allowed" : "pointer",
          }}
        >
          <span style={{ minWidth: 0, flex: 1 }}>
            {selected ? <CandidateRow candidate={selected} /> : <OwnAccountRow label={ownLabel} />}
          </span>
          <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 11, color: "#74788d", flexShrink: 0 }} />
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ width: "100%", maxHeight: 300, overflowY: "auto", padding: 6 }}>
          <Dropdown.Item as="button" type="button" active={!current} onClick={() => choose("")} style={{ borderRadius: 6, padding: "6px 8px" }}>
            <OwnAccountRow label={ownLabel} />
          </Dropdown.Item>
          {candidates.length > 0 && <Dropdown.Divider />}
          {candidates.map((c) => (
            <Dropdown.Item
              key={c.id}
              as="button"
              type="button"
              active={String(c.id) === String(current)}
              onClick={() => choose(String(c.id))}
              style={{ borderRadius: 6, padding: "6px 8px" }}
            >
              <CandidateRow candidate={c} />
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
      {current && !isEdit && (
        <div style={{ fontSize: 12, color: "#74788d", marginTop: 6 }}>
          {t("Backend.Post.Schedule.PublishOnHint", "Nothing is published until this person approves the post. Editing the post afterwards asks for approval again.")}
        </div>
      )}
    </div>
  );
};

const ScheduleFormView = ({
  formik,
  showPlanningForm,
  setshowPlanningForm,
  editData,
  CustomSaveButton = null,
  allPlatforms = false,
  candidates = [],
}) => {
  const { t } = useTranslation();

  const Save = () => {
    return CustomSaveButton ? (
      <CustomSaveButton/>
    ) : (
      <SaveButton formik={formik} />
    );
  };

  return (
    <NewModal
      title={t("Backend.Post.Schedule.ModalTitle")}
      show={showPlanningForm}
      setShow={setshowPlanningForm}
      onExit={() => {
        formik.resetForm({ values: { Schedule } });
      }}
      onShow={() => {
        editData
          ? formik.setValues(ScheduleForm.setEditData(editData))
          : formik.setValues(Schedule);
      }}
      ExtraButton={Save}
    >
      <Row>
        <Col>
          <DateTimeField
            formik={formik}
            label={t("Backend.Post.Schedule.ScheduleFor")}
            valueName={"local_user_time"}
          />
        </Col>
        <Col>
          <Timezones formik={formik} />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <PlatformMultiSelect
            formik={formik}
            label={t("Backend.Post.Schedule.Platformname")}
            allPlatforms={allPlatforms}
          />
        </Col>
      </Row>
      {(candidates.length > 0 || editData?.publish_as) && (
        <Row className="mt-3">
          <Col>
            <PublishAsSelect formik={formik} candidates={candidates} editData={editData} />
          </Col>
        </Row>
      )}
    </NewModal>
  );
};

export default ScheduleFormView;
